import {
  addTransition,
  clipEnd,
  secondsToFrame,
  transitionRange,
  type Clip,
  type Rational,
  type Sequence,
  type Track,
  type TransitionType,
} from '@ve/core';
import { memo, type DragEvent } from 'react';
import { openContextMenu, type MenuEntry } from '../../components/ContextMenu.tsx';
import {
  addTransitionAtSelection,
  deleteSelection,
  groupSelection,
  shortcutOf,
  soundClips,
  splitAtPlayhead,
  toggleClipEnabled,
  toggleClipsMuted,
  ungroupSelection,
} from '../../lib/commands.ts';
import { DND_TRANSITION } from '../../lib/dnd.ts';
import { usePendingDrops } from './drop.ts';
import { edit, useEditor } from '../../state/editor-store.ts';
import { transport } from '../../state/playback.ts';
import { toast } from '../../state/toast-store.ts';
import { TRANSITION_LABELS } from '../../lib/labels.ts';
import { tip } from '../../lib/tooltip.ts';
import { ClipView } from './ClipView.tsx';
import { beginMove, beginSlip, beginTrim, razorAt, zoneAt } from './interactions.ts';

export interface TrackLaneProps {
  track: Track;
  sequence: Sequence;
  pxPerFrame: number;
  fps: Rational;
  height: number;
  width: number;
  viewport: { left: number; right: number };
  selectedIds: string[];
  selectedTransitionId: string | null;
  onSnap(frame: number | null): void;
}

export const TrackLane = memo(function TrackLane({
  track,
  sequence,
  pxPerFrame,
  fps,
  height,
  width,
  viewport,
  selectedIds,
  selectedTransitionId,
  onSnap,
}: TrackLaneProps) {
  const frameAt = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    return Math.max(0, Math.round((clientX - rect.left) / pxPerFrame));
  };

  const onClipPointerDown = (e: React.PointerEvent, clip: Clip) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (track.locked) {
      toast.info(`Track ${track.name} is locked`);
      return;
    }
    const state = useEditor.getState();
    const project = state.project!;
    const ctx = { project, sequence, pxPerFrame: state.pxPerFrame, snapping: state.snapping, onSnap };
    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const zone = zoneAt(e.clientX - rect.left, rect.width);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;

    if (state.tool === 'razor') {
      razorAt(clip, clip.start + Math.round((e.clientX - rect.left) / pxPerFrame), ctx);
      return;
    }
    if (!state.selection.clipIds.includes(clip.id) || additive) state.toggleClipSelection(clip.id, additive);
    if (additive) return;

    if (state.tool === 'slip' && zone === 'body') beginSlip(e, clip, ctx);
    else if (zone === 'body') beginMove(e, clip, ctx);
    else {
      const mode =
        state.tool === 'ripple' ? 'ripple' : state.tool === 'roll' ? 'roll' : e.altKey ? 'ripple' : 'trim';
      beginTrim(e, clip, zone, mode, ctx);
    }
  };

  const onClipContextMenu = (e: React.MouseEvent, clip: Clip) => {
    e.preventDefault();
    const state = useEditor.getState();
    if (!state.selection.clipIds.includes(clip.id)) state.select({ clipIds: [clip.id] });
    const items: MenuEntry[] = [
      {
        label: 'Cut at playhead',
        description: 'Split the selected clips where the playhead is',
        shortcut: shortcutOf('split'),
        onSelect: splitAtPlayhead,
      },
      {
        label: 'Add crossfade',
        description: 'Dissolve into the neighbouring clip on the nearest cut',
        shortcut: shortcutOf('transition'),
        onSelect: () => addTransitionAtSelection(),
      },
      'separator',
      {
        label: clip.enabled ? 'Disable' : 'Enable',
        description: clip.enabled
          ? 'Hide and mute the clip without deleting it'
          : 'Make the clip visible and audible again',
        shortcut: shortcutOf('toggleEnabled'),
        onSelect: toggleClipEnabled,
      },
    ];
    const sound = soundClips(state.project!, useEditor.getState().selection.clipIds);
    if (sound.length > 0) {
      const unmute = sound.every((c) => c.muted);
      const count = sound.length > 1 ? ` ${sound.length} clips` : '';
      items.push({
        label: `${unmute ? 'Unmute' : 'Mute'}${count}`,
        description: unmute ? 'Make the selected clips audible again' : 'Silence the selected clips',
        shortcut: shortcutOf('toggleMuted'),
        onSelect: toggleClipsMuted,
      });
    }
    items.push({
      label: 'Group into chapter',
      description: 'Move the selected clips into a new chapter (nested timeline)',
      shortcut: shortcutOf('group'),
      onSelect: groupSelection,
    });
    if (clip.type === 'sequence') {
      items.push(
        {
          label: 'Open chapter',
          description: 'Edit the chapter in its own timeline tab',
          onSelect: () => state.openSequence(clip.sequenceId),
        },
        {
          label: 'Ungroup',
          description: 'Put the chapter’s clips back onto this timeline',
          shortcut: shortcutOf('ungroup'),
          onSelect: ungroupSelection,
        },
      );
    }
    items.push(
      'separator',
      {
        label: 'Delete',
        description: 'Remove the selected clips, leaving a gap',
        shortcut: shortcutOf('delete'),
        danger: true,
        onSelect: () => deleteSelection(false),
      },
      {
        label: 'Ripple delete',
        description: 'Remove the selected clips and close the gap',
        shortcut: shortcutOf('rippleDelete'),
        danger: true,
        onSelect: () => deleteSelection(true),
      },
    );
    openContextMenu(e.clientX, e.clientY, items);
  };

  // Lanes only handle transitions; media and files are handled by the timeline (see drop.ts).
  const onDragOver = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes(DND_TRANSITION)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    const transition = e.dataTransfer.getData(DND_TRANSITION) as TransitionType | '';
    if (!transition) return;
    e.preventDefault();
    e.stopPropagation();
    const frame = frameAt(e.clientX, e.currentTarget);
    // Nearest cut between touching clips within 40 px.
    let best: { from: Clip; dist: number } | null = null;
    track.clips.forEach((c, i) => {
      const next = track.clips[i + 1];
      if (!next || clipEnd(c) !== next.start) return;
      const dist = Math.abs(clipEnd(c) - frame) * pxPerFrame;
      if (dist < 40 && (!best || dist < best.dist)) best = { from: c, dist };
    });
    const found = best as { from: Clip; dist: number } | null;
    if (!found) return toast.info('Drop transitions onto a cut between two touching clips');
    const id = edit('Add transition', (p) =>
      addTransition(p, found.from.id, transition, secondsToFrame(1, fps)),
    );
    if (id) useEditor.getState().select({ transitionId: id });
  };

  const uploads = usePendingDrops((s) => s.drops).filter((d) => d.trackId === track.id);

  return (
    <div
      className={`lane lane-${track.kind}${track.locked ? ' locked' : ''}${track.hidden || track.muted ? ' dimmed' : ''}`}
      style={{ height, width }}
      data-track-id={track.id}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {track.clips.map((clip) => {
        const l = clip.start * pxPerFrame;
        const r = clipEnd(clip) * pxPerFrame;
        if (r < viewport.left - 200 || l > viewport.right + 200) return null;
        return (
          <div
            key={clip.id}
            className="clip-hit"
            onPointerDown={(e) => onClipPointerDown(e, clip)}
            onPointerMove={(e) => {
              const el = e.currentTarget as HTMLElement;
              const rect = el.getBoundingClientRect();
              const zone = zoneAt(e.clientX - rect.left, rect.width);
              const tool = useEditor.getState().tool;
              el.style.cursor =
                tool === 'razor'
                  ? 'crosshair'
                  : tool === 'slip' && zone === 'body'
                    ? 'ew-resize'
                    : zone === 'body'
                      ? 'grab'
                      : 'col-resize';
            }}
            onContextMenu={(e) => onClipContextMenu(e, clip)}
            onDoubleClick={() => {
              if (clip.type === 'sequence') useEditor.getState().openSequence(clip.sequenceId);
              else transport.seek(clip.start);
            }}
            style={{ position: 'absolute', left: l, width: Math.max(2, r - l), top: 0, height }}
          >
            <ClipView
              clip={clip}
              pxPerFrame={pxPerFrame}
              fps={fps}
              selected={selectedIds.includes(clip.id)}
              viewport={viewport}
              height={height - 2}
            />
          </div>
        );
      })}
      {track.transitions.map((tr) => {
        const range = transitionRange(track, tr);
        if (!range) return null;
        return (
          <div
            key={tr.id}
            className={`transition-mark${selectedTransitionId === tr.id ? ' selected' : ''}`}
            style={{ left: range.start * pxPerFrame, width: Math.max(6, tr.duration * pxPerFrame) }}
            {...tip(`${TRANSITION_LABELS[tr.type]} · ${tr.duration} frames — click to edit`)}
            onPointerDown={(e) => {
              e.stopPropagation();
              useEditor.getState().select({ transitionId: tr.id });
            }}
          />
        );
      })}
      {uploads.map((u) => (
        <div
          key={u.id}
          className="upload-ghost"
          style={{ left: u.frame * pxPerFrame, width: Math.max(24, u.frames * pxPerFrame) }}
          {...tip(`Uploading ${u.name} — it will be placed here when ready`)}
        >
          <span>
            {u.progress < 1 ? `Uploading ${Math.round(u.progress * 100)}%` : 'Analysing…'} · {u.name}
          </span>
          <div className="upload-ghost-bar" style={{ width: `${Math.round(u.progress * 100)}%` }} />
        </div>
      ))}
    </div>
  );
});
